import type { ColumnMetadata } from '../grid/types';

const PROJECT_ID_FIELD_NAMES = new Set(['projectid', 'ttproject', 'ttprojectid']);

const PHASE_FIELD_NAMES = new Set(['phase', 'phaseid', 'ttphase', 'ttphaseid', 'taskphaseid']);
const RELEASE_FIELD_NAMES = new Set(['ttprojectreleases', 'projectreleasesid']);
const EMPLOYEE_FIELD_NAMES = new Set(['crmrelationassigned', 'assignedrelationid', 'assignedid']);

export type ProjectScopedLookupFieldType = 'phase' | 'release' | 'employee' | null;

const normalize = (value?: unknown): string =>
  String(value ?? '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

export const getProjectIdFromEntity = (
  entity: Record<string, unknown> | undefined
): number | undefined => {
  if (!entity) return undefined;

  const candidates = [
    entity.projectId,
    entity.ProjectId,
    (entity.project as { id?: unknown } | undefined)?.id,
    entity.TtProject,
    entity.ttProject,
    entity.TtProjectId,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
};

export const getProjectIdFromMetadataDefaults = (
  fields: ColumnMetadata[] | undefined
): number | undefined => {
  if (!fields?.length) return undefined;

  const projectField = fields.find((field) =>
    PROJECT_ID_FIELD_NAMES.has(normalize(field.dataField))
  );

  const parsed = Number(projectField?.defaultValue);
  return !Number.isNaN(parsed) && parsed > 0 ? parsed : undefined;
};

export const shouldApplyProjectPhaseFilter = (field: ColumnMetadata): boolean => {
  return getProjectScopedLookupFieldType(field) === 'phase';
};

export const getProjectScopedLookupFieldType = (
  field: ColumnMetadata
): ProjectScopedLookupFieldType => {
  const dataField = normalize(field.dataField);
  const displayExpr = normalize(field.additionalData?.displayExpr);

  if (PHASE_FIELD_NAMES.has(dataField) || PHASE_FIELD_NAMES.has(displayExpr)) return 'phase';
  if (RELEASE_FIELD_NAMES.has(dataField) || RELEASE_FIELD_NAMES.has(displayExpr)) return 'release';
  if (EMPLOYEE_FIELD_NAMES.has(dataField) || EMPLOYEE_FIELD_NAMES.has(displayExpr)) {
    return 'employee';
  }

  return null;
};

export const shouldApplyProjectScopedLookupFilter = (field: ColumnMetadata): boolean => {
  return getProjectScopedLookupFieldType(field) !== null;
};

export const isCompatibleLookupSample = (
  sampleFields: string[] | undefined,
  dataField?: string,
  displayFieldName?: string
): boolean => {
  if (!sampleFields || sampleFields.length === 0) return true;

  const normalizedDataField = normalize(dataField);
  const normalizedDataFieldNoId = normalizedDataField.replace(/id$/i, '');
  const normalizedDisplay = normalize(displayFieldName);

  for (const fieldName of sampleFields) {
    const normalizedName = normalize(fieldName);

    if (normalizedDataField && normalizedName === normalizedDataField) return true;
    if (normalizedDataFieldNoId && normalizedName === normalizedDataFieldNoId) return true;
    if (normalizedDisplay && normalizedName === normalizedDisplay) return true;
    if (normalizedDataField && normalizedName.endsWith(normalizedDataField)) return true;
    if (normalizedDisplay && normalizedName.endsWith(normalizedDisplay)) return true;
    if (normalizedDataField && normalizedName.includes(normalizedDataField)) return true;
    if (normalizedDisplay && normalizedName.includes(normalizedDisplay)) return true;
  }

  return false;
};

export const getLookupMapKeys = (field: ColumnMetadata): string[] => {
  const keys = [field.dataField, field.key].filter(
    (key): key is string => typeof key === 'string' && key.trim().length > 0
  );

  return Array.from(new Set(keys));
};
